// ─── Utilidades de fecha seguras para zona horaria local ─────────────────────
// Evitan el bug de toISOString() que usa UTC y puede cambiar la fecha
// después de cierta hora en zonas horarias negativas (ej: Chile UTC-3/-4).

/**
 * Devuelve la fecha en formato YYYY-MM-DD usando la zona horaria local.
 * Reemplaza `new Date().toISOString().split('T')[0]` que usa UTC.
 * @param {Date|string|number|object} value - Date, Firestore Timestamp, string de fecha, o timestamp
 * @returns {string} fecha en formato YYYY-MM-DD
 */
export const toLocalDateStr = (value) => {
  if (!value) return '';
  let date;
  if (value.toDate && typeof value.toDate === 'function') {
    date = value.toDate();
  } else if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }
  if (isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Devuelve la fecha de hoy en formato YYYY-MM-DD usando zona horaria local.
 * @returns {string}
 */
export const todayLocalStr = () => toLocalDateStr(new Date());

/**
 * Suma N días a la fecha actual y devuelve en formato YYYY-MM-DD (zona local).
 * Reemplaza `new Date(); d.setDate(...); d.toISOString().slice(0, 10)`.
 * @param {number} n - días a sumar (puede ser negativo)
 * @returns {string}
 */
export const addDaysLocal = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return toLocalDateStr(d);
};

/**
 * Determina si una agenda (con recurrencia) está activa para una fecha dada.
 * @param {object} schedule - Documento de professionalSchedules
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD
 * @returns {boolean}
 *
 * Recurrencia soportada:
 * - 'continuous' (default): la agenda se repite cada semana para el día definido.
 * - 'biweekly': se repite cada dos semanas desde startDate para el día definido.
 * - 'monthly': se repite una vez por mes en la semana ordinal (first/second/third/last)
 *   correspondiente al día de la semana definido.
 */
export const isScheduleActiveOnDate = (schedule, dateStr) => {
  if (!schedule || !dateStr) return false;
  const startStr = schedule.startDate;
  const endStr = schedule.endDate;
  if (startStr && dateStr < startStr) return false;
  if (endStr && dateStr > endStr) return false;

  const recurrenceType = schedule.recurrenceType || 'continuous';

  if (recurrenceType === 'continuous') return true;

  // Parsear fechas como locales (mediodía) para evitar bug UTC
  const start = new Date(`${startStr}T12:00:00`);
  const target = new Date(`${dateStr}T12:00:00`);

  if (recurrenceType === 'biweekly') {
    // Diferencia en días desde startDate
    const diffDays = Math.round((target - start) / 86400000);
    if (diffDays < 0) return false;
    // Semana 0 = días 0-6, semana 1 = días 7-13, etc.
    // Activo solo en semanas pares (0, 2, 4, ...)
    const weekIndex = Math.floor(diffDays / 7);
    return weekIndex % 2 === 0;
  }

  if (recurrenceType === 'monthly') {
    const weekOrdinal = schedule.recurrenceWeekOrdinal || 'first';
    // Encontrar la fecha correspondiente al día de la semana del mes
    const targetDayOfWeek = target.getDay(); // 0=Dom..6=Sáb
    const year = target.getFullYear();
    const month = target.getMonth();

    // Encontrar todas las fechas del mes que caen en ese día de la semana
    const matchingDates = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const candidate = new Date(year, month, d, 12, 0, 0);
      if (candidate.getDay() === targetDayOfWeek) {
        matchingDates.push(candidate.getDate());
      }
    }

    let targetDayOfMonth;
    if (weekOrdinal === 'first') targetDayOfMonth = matchingDates[0];
    else if (weekOrdinal === 'second') targetDayOfMonth = matchingDates[1];
    else if (weekOrdinal === 'third') targetDayOfMonth = matchingDates[2];
    else if (weekOrdinal === 'last') targetDayOfMonth = matchingDates[matchingDates.length - 1];

    if (targetDayOfMonth == null) return false;
    return target.getDate() === targetDayOfMonth;
  }

  return true;
};
